import type {
  CustomerDetail,
  CustomerServiceAddressDetail,
  CustomerServiceAddressInput,
  ICustomer,
} from "@mytour/shared";
import {GeocodeSourceSchema} from "@mytour/shared";
import {z} from "zod";

export type CustomerWithServiceAddresses = ICustomer &
  Partial<Pick<CustomerDetail, "serviceAddresses" | "serviceAddressesVersion">>;

export const serviceAddressFormSchema = z
  .object({
    managerEnabled: z.boolean(),
    managerFamilyName: z.string(),
    managerFirstName: z.string(),
    managerEmail: z.string(),
    managerPhone: z.string(),
    managerGender: z.enum(["", "mr", "mrs"]),
    label: z.string().max(255, "Le libellé ne peut pas dépasser 255 caractères"),
    serviceAddress: z.string().min(1, "Ce champ est obligatoire"),
    serviceAddress2: z.string(),
    serviceZip: z
      .string()
      .regex(/^[0-9]{5}$/, "Le code postal en France est une suite de cinq chiffres"),
    serviceCity: z.string().min(1, "Ce champ est obligatoire"),
    serviceLat: z.number().nullable().optional(),
    serviceLong: z.number().nullable().optional(),
    serviceGeocodeSource: GeocodeSourceSchema.nullable().optional(),
  })
  .superRefine((values, context) => {
    if (!values.managerEnabled) {
      return;
    }
    for (const field of [
      "managerFamilyName",
      "managerFirstName",
      "managerEmail",
      "managerPhone",
    ] as const) {
      if (values[field].length > 255) {
        context.addIssue({
          code: "custom",
          path: [field],
          message: "Ce champ ne peut pas dépasser 255 caractères.",
        });
      }
    }
    if (!values.managerFamilyName.trim()) {
      context.addIssue({
        code: "custom",
        path: ["managerFamilyName"],
        message: "Saisissez un nom.",
      });
    }
    if (!z.string().email().safeParse(values.managerEmail.trim()).success) {
      context.addIssue({
        code: "custom",
        path: ["managerEmail"],
        message: "Saisissez une adresse email valide.",
      });
    }
    if (values.managerPhone.trim() && !/^[+\d() .-]{6,24}$/.test(values.managerPhone.trim())) {
      context.addIssue({
        code: "custom",
        path: ["managerPhone"],
        message: "Vérifiez le numéro de téléphone.",
      });
    }
  });

export function serviceAddressSchemaForCustomerEmail(email: string) {
  return serviceAddressFormSchema.superRefine((values, context) => {
    if (
      values.managerEnabled &&
      values.managerEmail.trim().toLowerCase() === email.trim().toLowerCase()
    ) {
      context.addIssue({
        code: "custom",
        path: ["managerEmail"],
        message: "Utilisez un email différent de celui du client.",
      });
    }
  });
}

export type ServiceAddressFormData = z.infer<typeof serviceAddressFormSchema>;
export type ServiceAddressDraft = {
  id: string;
  initial: CustomerServiceAddressDetail | null;
  values: ServiceAddressFormData;
  valid: boolean;
  hasErrors: boolean;
  resolved: boolean;
};

export function createServiceAddressDraft(
  address: CustomerServiceAddressDetail | null = null
): ServiceAddressDraft {
  return {
    id: address?.id ?? crypto.randomUUID(),
    initial: address,
    values: {
      managerEnabled: !!address?.manager,
      managerFamilyName: address?.manager?.familyName ?? "",
      managerFirstName: address?.manager?.firstName ?? "",
      managerEmail: address?.manager?.email ?? "",
      managerPhone: address?.manager?.phone ?? "",
      managerGender: address?.manager?.gender ?? "",
      label: address?.label ?? "",
      serviceAddress: address?.address ?? "",
      serviceAddress2: address?.address2 ?? "",
      serviceZip: (address?.zip ?? "").replace(/\s/g, ""),
      serviceCity: address?.city ?? "",
    },
    valid: false,
    hasErrors: false,
    resolved: false,
  };
}

export function getCustomerServiceAddressDrafts(
  customer: CustomerWithServiceAddresses | null
): ServiceAddressDraft[] {
  if (customer?.serviceAddresses !== undefined) {
    return customer.serviceAddresses
      .filter((address) => !address.isRemoved)
      .map(createServiceAddressDraft);
  }
  if (!customer) {
    return [createServiceAddressDraft()];
  }
  if (!customer.serviceAddress && !customer.serviceZip && !customer.serviceCity) {
    return [];
  }
  return [
    createServiceAddressDraft({
      id: customer.defaultServiceAddressId ?? crypto.randomUUID(),
      label: null,
      address: customer.serviceAddress ?? "",
      address2: customer.serviceAddress2 ?? null,
      zip: customer.serviceZip ?? "",
      city: customer.serviceCity ?? "",
      latitude: customer.serviceLat ?? null,
      longitude: customer.serviceLong ?? null,
      geocodeScore: customer.serviceGeocodeScore ?? null,
      geocodePrecision: customer.serviceGeocodePrecision ?? null,
      geocodeLabel: customer.serviceGeocodeLabel ?? null,
      geocodeSource: customer.serviceGeocodeSource ?? null,
      isDefault: true,
      isUsed: false,
      isRemoved: false,
    }),
  ];
}

export function serviceAddressDraftToInput(
  draft: ServiceAddressDraft
): CustomerServiceAddressInput {
  const {values} = draft;
  return {
    id: draft.id,
    manager: values.managerEnabled
      ? {
          familyName: values.managerFamilyName.trim(),
          firstName: values.managerFirstName.trim() || undefined,
          email: values.managerEmail.trim(),
          phone: values.managerPhone.trim() || undefined,
          gender: values.managerGender || undefined,
        }
      : null,
    label: values.label.trim() || null,
    address: values.serviceAddress.trim(),
    address2: values.serviceAddress2.trim() || null,
    zip: values.serviceZip.replace(/\s/g, ""),
    city: values.serviceCity.trim(),
    ...(values.serviceGeocodeSource !== undefined
      ? {
          geocodeSource: values.serviceGeocodeSource,
          latitude: values.serviceLat != null ? String(values.serviceLat) : null,
          longitude: values.serviceLong != null ? String(values.serviceLong) : null,
        }
      : {}),
  };
}
